import React, {
	CSSProperties,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "uikit";

import { StyledColumn, StyledRow, StyledSpan, ValueRange } from "../common";
import File from "../../services/File";
import { browse } from "../../utils/file";
import { trimAudioFile } from "../../utils/trimAudio";
import {
	SOUND_ACCEPT,
	SOUND_FORMATS,
	SOUND_MAX_SECONDS,
	createSoundPlayer,
} from "../../utils/notificationSound";

import type { SoundSettingValue } from "../../redux/reducers/settings/sounds";

const tPath = "settings.interface.sounds.field";

const hintStyle: CSSProperties = {
	color: "#647079",
	fontSize: 12,
	whiteSpace: "normal",
};

const errorStyle: CSSProperties = {
	color: "#f83528",
	fontSize: 12,
	whiteSpace: "normal",
};

const volumeLabelStyle: CSSProperties = { minWidth: 40, textAlign: "right" };

/** Only one preview plays at a time across the page. */
let activePreview: ReturnType<typeof createSoundPlayer> | null = null;

/**
 * One sound setting: volume, file, preview and restoring the standard sound.
 * The same block is used on the Sounds tab and in the order settings.
 */
const SoundSetting: React.FC<SoundSetting.Props> = ({
	value,
	onChange,
	defaultSoundUrl,
	disabled = false,
}) => {
	const { t } = useTranslation();

	// Upload is async: the volume or the handler may change meanwhile.
	const valueRef = useRef(value);
	valueRef.current = value;
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const player = useMemo(() => createSoundPlayer(), []);

	useEffect(
		() => () => {
			player.stop();
			if (activePreview === player) activePreview = null;
		},
		[player],
	);

	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const onVolumeChange = useCallback(
		(volume: number) => {
			player.setVolume(volume);
			onChangeRef.current({ ...valueRef.current, volume });
		},
		[player],
	);

	const onUpload = useCallback(async () => {
		setError(null);

		let picked: globalThis.File;
		try {
			picked = (await browse({
				type: SOUND_ACCEPT,
			})) as globalThis.File;
		} catch {
			return;
		}

		setUploading(true);
		try {
			const { file } = await trimAudioFile(picked, SOUND_MAX_SECONDS);
			const model = new File.Model(file);
			await model.upload();

			if (!model.id) throw new Error("upload failed");

			onChangeRef.current({
				...valueRef.current,
				sound: { fileId: model.id, name: picked.name },
			});
		} catch (e) {
			// eslint-disable-next-line no-console
			console.warn("[SoundSetting] upload failed", e);
			setError(t(`${tPath}.error`) ?? "");
		} finally {
			setUploading(false);
		}
	}, [t]);

	const onPlay = useCallback(() => {
		if (activePreview !== player) activePreview?.stop();
		activePreview = player;

		const { sound, volume } = valueRef.current;
		player.play(sound, defaultSoundUrl, volume).catch(() => undefined);
	}, [defaultSoundUrl, player]);

	const onReset = useCallback(() => {
		setError(null);
		onChangeRef.current({ ...valueRef.current, sound: null });
	}, []);

	const controlsDisabled = disabled || uploading;

	return (
		<StyledColumn gap="10px">
			<StyledRow gap="10px" alignItems="center">
				<StyledSpan>{t(`${tPath}.volume`) ?? ""}:</StyledSpan>
				<ValueRange
					range="2"
					w="240px"
					min={0}
					max={100}
					step={1}
					value={value.volume}
					disabled={disabled}
					onChange={onVolumeChange}
				/>
				<StyledSpan style={volumeLabelStyle}>
					{value.volume}%
				</StyledSpan>
			</StyledRow>

			<StyledSpan>
				{t(`${tPath}.sound`) ?? ""}:{" "}
				{value.sound?.name || (t(`${tPath}.defaultSound`) ?? "")}
			</StyledSpan>

			<StyledRow gap="10px" alignItems="center">
				<Button.Button
					text={t(`${tPath}.upload`) ?? ""}
					variant="secondary"
					disabled={controlsDisabled}
					onClick={onUpload}
				/>
				<Button.Button
					title={t(`${tPath}.play`) ?? ""}
					variant="primary"
					icon={<Icon id="play" size={20} />}
					disabled={controlsDisabled}
					onClick={onPlay}
				/>
			</StyledRow>

			{value.sound && (
				<StyledRow>
					<Button.Button
						text={t(`${tPath}.reset`) ?? ""}
						variant="secondary"
						disabled={controlsDisabled}
						onClick={onReset}
					/>
				</StyledRow>
			)}

			<StyledSpan style={hintStyle}>
				{t(`${tPath}.formats`, {
					formats: SOUND_FORMATS.join(", "),
				}) ?? ""}
			</StyledSpan>
			<StyledSpan style={hintStyle}>
				{t(`${tPath}.maxDuration`, {
					seconds: SOUND_MAX_SECONDS,
				}) ?? ""}
			</StyledSpan>
			{error && <StyledSpan style={errorStyle}>{error}</StyledSpan>}
		</StyledColumn>
	);
};

declare namespace SoundSetting {
	interface Props {
		value: SoundSettingValue;
		onChange: (value: SoundSettingValue) => void;
		defaultSoundUrl: string;
		disabled?: boolean;
	}
}

export default SoundSetting;
